import { useIntl } from "react-intl";

import Select from "@rio-cloud/rio-uikit/Select";
import DatePicker from "@rio-cloud/rio-uikit/DatePicker";
import { BudgetExportButton } from "./BudgetExportButton";
import { BudgetKanbanStatusFilter, IBudgetKanbanFilters } from "../../../../models/budgetV2";

import { parseYMD, toLocalDateTimeString } from "../../../../utils/dateUtils";

import "../index.scss";

interface BudgetKanbanFiltersProps {
  filters: IBudgetKanbanFilters;
  persona?: string;
  setFilters: React.Dispatch<React.SetStateAction<IBudgetKanbanFilters>>;
  onCreateBudget?: () => void;
}

const BudgetKanbanFilters = ({
  filters,
  persona,
  setFilters,
  onCreateBudget,
}: BudgetKanbanFiltersProps) => {
  const intl = useIntl();

  return (
    <div className="flex gap-4 mb-4 w-full max-w-[2048px] mx-auto">
      <div className={`gap-3 w-full md:flex`}>
        <div className={`form-group shrink-0 mb-0 ${persona === 'CONSULTANT' ? 'display-none' : ''}`}>
          <label className="text-sm font-medium mb-1 block h-[20px]">&nbsp;</label>
          <button
            type="button"
            className={`btn bg-primary flex items-center justify-center gap-2 h-[34px]`}
            onClick={onCreateBudget}
          >
            <span className="rioglyph rioglyph-plus"></span>
            {intl.formatMessage({ id: "intl-msg:budgets.kanban.filters.createBudget" })}
          </button>
        </div>

        <div className={`form-group w-full`}>
          <label htmlFor="searchChassisFilter" className="text-sm font-medium mb-1 block h-[20px]">
            {intl.formatMessage({ id: "intl-msg:budgets.kanban.filters.chassisSearchPlaceholder" })}
          </label>      
          <div className="input-group">
            <span className="input-group-addon" style={{ display: "flex", alignItems: "center" }}>
              <span className="rioglyph rioglyph-search" />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder={intl.formatMessage({ id: "intl-msg:budgets.kanban.filters.chassisSearchPlaceholder" })}
              id="chassisSearch"
              value={filters.chassis}
              onChange={(e) => setFilters(prev => ({ ...prev, chassis: e.target.value }))}
              maxLength={17}
              style={{ height: "34px", minHeight: "34px", maxHeight: "34px", lineHeight: "34px" }}
            />
          </div>
        </div>

        <div className={`form-group md:w-[30%]`}>
          <label htmlFor="statusFilter" className="text-sm font-medium mb-1 block h-[20px]">
            {intl.formatMessage({ id: "intl-msg:budgets.kanban.filters.status" })}
          </label>
          <div style={{ height: "34px", minHeight: "34px", maxHeight: "34px" }}>
            <Select
              id="statusFilter"
              options={[
                { id: '', label: intl.formatMessage({ id: "intl-msg:budgets.kanban.filters.all" }) },
                { id: "SENT", label: intl.formatMessage({ id: "intl-msg:budgets.kanban.filters.sent" }) },
                { id: "UPDATED", label: intl.formatMessage({ id: "intl-msg:budgets.kanban.filters.updated" }) },
                { id: "COUNTER_PROPOSED", label: intl.formatMessage({ id: "intl-msg:budgets.kanban.filters.counterproposal" }) },
                { id: "APPROVED", label: intl.formatMessage({ id: "intl-msg:budgets.kanban.filters.approved" }) },
                { id: "REJECTED", label: intl.formatMessage({ id: "intl-msg:budgets.kanban.filters.rejected" }) },
              ]}
              value={[filters.status ?? '']}
              onChange={(e) => setFilters(prev => ({
                ...prev,
                status: e?.id ? e.id as BudgetKanbanStatusFilter : undefined,
              }))}
              className="min-w-[150px]"
            />
          </div>
        </div>

        <div className={`form-group md:flex-shrink-0`}>
          <label className="text-sm font-medium mb-1 block h-[20px]">{intl.formatMessage({ id: "intl-msg:budgets.kanban.filters.updateDate" })}</label>
          <div className={`flex items-center flex-row gap-3`} style={{ height: "34px", minHeight: "34px", maxHeight: "34px" }}>
            <div className={`md:min-w-[180px] w-full`} style={{ height: "34px", minHeight: "34px", maxHeight: "34px" }}>
              <DatePicker
                id="updateDateStart"
                value={filters.startUpdatedAt ? parseYMD(filters.startUpdatedAt) : undefined}
                onChange={(e) => setFilters(prev => ({
                  ...prev,
                  startUpdatedAt: !e
                    ? undefined
                    : toLocalDateTimeString(typeof e === "string" ? e : e.format("YYYY-MM-DD")),
                }))}
                locale="pt-BR"
                dateFormat="DD-MM-YYYY"
                timeFormat={false}
                mandatory={false}
                clearableInput={true}
                className="date-picker-large"
              />
            </div>
            <span className={`text-sm whitespace-nowrap`} style={{ lineHeight: "34px" }}>{intl.formatMessage({ id: "intl-msg:budgets.kanban.filters.until" })}</span>
            <div className={`budget-datepicker-end md:min-w-[180px] w-full`} style={{ height: "34px", minHeight: "34px", maxHeight: "34px" }}>
              <DatePicker
                id="updateDateEnd"
                value={filters.endUpdatedAt ? parseYMD(filters.endUpdatedAt) : undefined}
                onChange={(e) => setFilters(prev => ({
                  ...prev,
                  endUpdatedAt: !e
                    ? undefined
                    : toLocalDateTimeString(typeof e === "string" ? e : e.format("YYYY-MM-DD"), true),
                }))}
                locale="pt-BR"
                dateFormat="DD-MM-YYYY"
                timeFormat={false}
                mandatory={false}
                clearableInput={true}
                className="date-picker-large"
              />
            </div>
          </div>
        </div>

        {persona === 'CONSULTANT'|| persona === 'TOWER' ? 
          <BudgetExportButton 
            chassis={filters.chassis}
            startUpdatedAt={filters.startUpdatedAt}
            endUpdatedAt={filters.endUpdatedAt}
          /> : null}
      </div>
    </div>
  );
};

export default BudgetKanbanFilters;
